import { StorageAdapter } from '../../storage/StorageAdapter'
import { getTransport } from '../transport'
import { apiBases } from '../api-bases'
import { registerCatalog, pullCatalog, getScope, setPostFormsHook } from './pull-engine'
import { dbgWarn } from '../../logger'

/**
 * catalogs.js — los 10 DESCRIPTORES Tier B + registerAllCatalogs() + prefetchFormChildren()
 * (PULL-02 / PULL-03 / B2). Enchufan al engine de 05-01 (registerCatalog/pullCatalog/getScope).
 *
 * COSTURAS (LOCKED):
 *   - Red SOLO via getTransport().get(url,{params}) dentro de cada descriptor.fetchPage (PUSH-05).
 *     NO se importa axios ni src/services/* aqui (las URLs/params se COPIARON de esos services,
 *     verificadas en 05-RESEARCH.md inventory; los services NO se importan).
 *   - SQL SOLO via StorageAdapter (el engine/upsert lo usa; este archivo no emite SQL crudo salvo
 *     la lectura de c_forms/c_form_groups en prefetch, via el `db` inyectado = StorageAdapter).
 *   - Cada descriptor respeta el SHAPE exacto de 05-01:
 *       {name, table, ttl, lazy, enabled, keyColumns, columns, needsScope, sinceField, fetchPage, mapRow}
 *     fetchPage devuelve el BODY CRUDO (el engine corre extractPage). mapRow(row,scope) keyed por columns.
 *
 * BASES DE URL (de src/api/index.js):
 *   - GRAPHQL_URL = config('gql.url') + 'api/'  → base gql de los 9 catalogos (forms/groups/items/
 *     locations/users/roles/areas/hierarchies/catalog_elements).
 *   - URL = config('api.url') + 'api/'          → backend VIEJO, SOLO para c_tags (gated-disabled).
 *   Se resuelven en runtime (no en import) via apiBases() para que vitest no arrastre src/config.
 *
 * SCOPE (I6): el engine corre getScope() ANTES de fetchPage, asi que cuando un descriptor lee
 * scope.organizationId la sesion ya esta resuelta o el pull se salto limpio — fetchPage nunca
 * deferencia un scope undefined por su cuenta.
 */

// TTLs (de v1, tunables). Exportado para tests/UI.
export const TIER_B_TTLS = {
  forms: 6 * 3600000, // 6h
  form_groups: 6 * 3600000,
  form_items: 6 * 3600000,
  locations: 6 * 3600000,
  users: 12 * 3600000, // 12h
  roles: 24 * 3600000, // 24h
  areas: 12 * 3600000,
  hierarchies: 12 * 3600000,
  tags: 12 * 3600000,
  catalog_elements: 12 * 3600000
}

// Timeout generoso para pulls de catalogo: payloads grandes (forms ~1MB) sobre backends/redes
// lentas pueden tardar >2min; el pull es background/best-effort asi que 5min acotado es correcto.
// (El default del transport, 120s, aplica al push/interactivo.)
const PULL_TIMEOUT = { timeout: 300000 }

const PAGE_LIMIT = 200

// apiBases (runtime, NO en import) ahora vive en sync/api-bases.js.

// ─── Flag hook para c_tags (B5) ───────────────────────────────────────────────
// Permite a un mantenedor flipear el pull de tags SIN cambiar codigo, una vez que el backend
// reubique tags a graphql-api (preferido) o se confirme auth del viejo backend via getTransport().
// Default = DESHABILITADO (c_tags se registra con enabled:false). Si existe un sistema de flags
// se lee aqui; si no, queda false.
function tagsPullEnabled () {
  try {
    // Hook opcional; si no hay infra de flags, queda deshabilitado.
    if (typeof globalThis !== 'undefined' && globalThis.__OFFLINE_TAGS_PULL__ === true) return true
  } catch {
    // sin flags => disabled
  }
  return false
}

// ─── Los 10 descriptores ──────────────────────────────────────────────────────

/** 1) c_forms — operator-scoped /api/forms (los forms ASIGNADOS = lo que prefetchFormChildren itera). */
const cForms = {
  name: 'forms',
  table: 'c_forms',
  ttl: TIER_B_TTLS.forms,
  keyColumns: ['id'],
  columns: ['id', 'organization_id', 'data', 'updated_at'],
  sinceField: 'updated_at',
  async fetchPage (scope, page) {
    const { gql } = await apiBases()
    const res = await getTransport().get(`${gql}forms`, { ...PULL_TIMEOUT, params: { limit: PAGE_LIMIT, page } })
    return res.data
  },
  mapRow (r, scope) {
    return {
      id: r.id,
      organization_id: (scope && scope.organizationId) != null ? scope.organizationId : r.organization_id,
      data: JSON.stringify(r),
      updated_at: r.updated_at
    }
  }
}

/**
 * 2) c_form_groups — LAZY (fan-out por-form es costoso, Open Q4). NO se pulea en el loop generico;
 * prefetchFormChildren lo calienta para los forms del operador, y F5/UI puede invocar pullCatalog
 * con el formId on-demand. El formId viaja en scope.formId (prefetch lo inyecta) porque el engine
 * de 05-01 solo pasa (scope,page,since) a fetchPage. extractPage tolera el doble-wrap data.data.data.
 */
const cFormGroups = {
  name: 'form_groups',
  table: 'c_form_groups',
  ttl: TIER_B_TTLS.form_groups,
  lazy: true,
  keyColumns: ['id'],
  columns: ['id', 'form_id', 'data'],
  // SCOPED full-refresh: borra SOLO los groups de este form (no la tabla entera), para no tumbar los
  // groups ya cacheados de otros forms durante el prefetch secuencial.
  scopedDelete (scope) {
    return { whereSql: 'form_id=?', whereParams: [scope && scope.formId] }
  },
  async fetchPage (scope) {
    const formId = scope && scope.formId
    const { gql } = await apiBases()
    const res = await getTransport().get(`${gql}forms/${formId}/groups`, PULL_TIMEOUT)
    return res.data
  },
  mapRow (r, scope) {
    return { id: r.id, form_id: (scope && scope.formId) != null ? scope.formId : r.form_id, data: JSON.stringify(r) }
  }
}

/**
 * 3) c_form_items — LAZY. Calentado por prefetchFormChildren por-grupo; lazy por grupo activo si no.
 * GET {gql}/api/forms/items/{groupId} → array. El groupId viaja en scope.groupId.
 */
const cFormItems = {
  name: 'form_items',
  table: 'c_form_items',
  ttl: TIER_B_TTLS.form_items,
  lazy: true,
  keyColumns: ['id'],
  columns: ['id', 'group_id', 'data'],
  scopedDelete (scope) {
    return { whereSql: 'group_id=?', whereParams: [scope && scope.groupId] }
  },
  async fetchPage (scope) {
    const groupId = scope && scope.groupId
    const { gql } = await apiBases()
    const res = await getTransport().get(`${gql}forms/items/${groupId}`, PULL_TIMEOUT)
    return res.data
  },
  mapRow (r, scope) {
    return { id: r.id, group_id: (scope && scope.groupId) != null ? scope.groupId : r.group_id, data: JSON.stringify(r) }
  }
}

/** 4) c_locations — Laravel paginate, ~2.4k. needsScope (org_id). */
const cLocations = {
  name: 'locations',
  table: 'c_locations',
  ttl: TIER_B_TTLS.locations,
  needsScope: true,
  keyColumns: ['id'],
  columns: ['id', 'org_id', 'name', 'data'],
  async fetchPage (scope, page) {
    const { gql } = await apiBases()
    const res = await getTransport().get(`${gql}locations`, { ...PULL_TIMEOUT, params: { limit: PAGE_LIMIT, page } })
    return res.data
  },
  mapRow (r, scope) {
    return { id: r.id, org_id: scope && scope.organizationId, name: r.name, data: JSON.stringify(r) }
  }
}

/** 5) c_users — Laravel paginate, ~6k. needsScope (organization_id en query + org_id en fila). */
const cUsers = {
  name: 'users',
  table: 'c_users',
  ttl: TIER_B_TTLS.users,
  needsScope: true,
  keyColumns: ['id'],
  columns: ['id', 'org_id', 'full_name', 'data'],
  async fetchPage (scope, page) {
    const { gql } = await apiBases()
    const res = await getTransport().get(`${gql}users/`, {
      ...PULL_TIMEOUT,
      params: { limit: PAGE_LIMIT, page, organization_id: scope && scope.organizationId }
    })
    return res.data
  },
  mapRow (r, scope) {
    const fullName = r.full_name != null ? r.full_name : [r.first_name, r.last_name].filter(Boolean).join(' ')
    return { id: r.id, org_id: scope && scope.organizationId, full_name: fullName, data: JSON.stringify(r) }
  }
}

/** 6) c_roles — single high-limit page por org. needsScope (orgId en la URL). */
const cRoles = {
  name: 'roles',
  table: 'c_roles',
  ttl: TIER_B_TTLS.roles,
  needsScope: true,
  keyColumns: ['id'],
  columns: ['id', 'name', 'data'],
  async fetchPage (scope) {
    const { gql } = await apiBases()
    const orgId = scope && scope.organizationId
    const res = await getTransport().get(`${gql}role/fetch/${orgId}`, { ...PULL_TIMEOUT, params: { limit: 999 } })
    return res.data
  },
  mapRow (r) {
    return { id: r.id, name: r.name, data: JSON.stringify(r) }
  }
}

/**
 * 7) c_areas — GET {gql}/api/fetchAreas?category= devuelve NAME STRINGS (VERIFIED). NAME-KEYED:
 * id queda NULL (NO se inventan ids enteros). c_areas tiene `id INTEGER PRIMARY KEY` pero NINGUN
 * UNIQUE sobre name, asi que ON CONFLICT(name) seria invalido en SQLite y NO se puede agregar un
 * UNIQUE sin migracion (prohibido en F4). Por eso keyColumns:[] => INSERT plano sin ON CONFLICT
 * (seguro: el full-refresh borra c_areas ANTES del INSERT; los names del set son unicos). El
 * conflict-target conceptual sigue siendo `name`.
 * TODO (B6): cuando fetchAreas devuelva objetos {id,name}, cambiar keyColumns a ['id'] y mapear
 * r.id real (entonces ON CONFLICT(id) vuelve a ser valido contra la PK). NO inventar ids ahora.
 */
const cAreas = {
  name: 'areas',
  table: 'c_areas',
  ttl: TIER_B_TTLS.areas,
  keyColumns: [], // name-keyed sin UNIQUE(name) => INSERT plano + full-replace (ver header)
  columns: ['id', 'category', 'name', 'data'],
  async fetchPage (scope) {
    const { gql } = await apiBases()
    const res = await getTransport().get(`${gql}fetchAreas`, { ...PULL_TIMEOUT, params: { category: (scope && scope.category) || undefined }
    })
    return res.data
  },
  mapRow (name, scope) {
    const isObj = name && typeof name === 'object'
    return {
      id: null,
      category: (scope && scope.category) != null ? scope.category : null,
      name: isObj ? name.name : name,
      data: JSON.stringify(name)
    }
  }
}

/**
 * 8) c_hierarchies — DOS llamadas fusionadas en una tabla via `kind`:
 *   GET {gql}/api/task-hierarchy/sites        (kind='site',        parent_id=null)
 *   GET {gql}/api/task-hierarchy/subcategories (kind='subcategory', parent_id de la respuesta)
 *
 * ATOMICIDAD DE LAS DOS LLAMADAS (W5 — LOCKED): NO se envuelve ninguna llamada en su propio
 * try/catch. Se emiten ambas (await secuencial) y si CUALQUIERA lanza, la excepcion PROPAGA fuera
 * de fetchPage. El try/catch best-effort por-catalogo del engine (bootstrap/revalidate) la atrapa y
 * salta c_hierarchies entero — la tabla NUNCA queda a medio poblar con solo sites o solo subcats.
 * (Si sites OK pero subcategories lanza, el array combinado nunca se devuelve, no hay upsert, el
 * cache previo queda intacto.)
 */
const cHierarchies = {
  name: 'hierarchies',
  table: 'c_hierarchies',
  ttl: TIER_B_TTLS.hierarchies,
  keyColumns: ['id'],
  columns: ['id', 'kind', 'parent_id', 'name', 'data'],
  async fetchPage () {
    const { gql } = await apiBases()
    const t = getTransport()
    // Ambas llamadas SIN try/catch propio: si cualquiera lanza, fetchPage propaga (W5).
    const sitesRes = await t.get(`${gql}task-hierarchy/sites`)
    const subsRes = await t.get(`${gql}task-hierarchy/subcategories`)
    const sites = unwrapArray(sitesRes.data).map((r) => ({ ...r, __kind: 'site' }))
    const subs = unwrapArray(subsRes.data).map((r) => ({ ...r, __kind: 'subcategory' }))
    return sites.concat(subs) // array plano => extractPage = una pagina, hasMore:false
  },
  mapRow (r) {
    const parentId = r.__kind === 'site' ? null : (r.parent_id != null ? r.parent_id : (r.site_id != null ? r.site_id : null))
    return { id: r.id, kind: r.__kind, parent_id: parentId, name: r.name, data: JSON.stringify(stripInternal(r)) }
  }
}

/**
 * 9) c_tags — GATED/DISABLED. enabled resuelto en registro (default false; tagsPullEnabled() permite
 * flipear via flag SIN cambiar codigo). Fuente = backend VIEJO.
 *
 * TODO (LOCKED — B5): c_tags es el UNICO blocker real (05-RESEARCH.md B5). La fuente es el backend
 * Laravel VIEJO (`GET {OLD}/api/action-plans/tags`), AUSENTE de graphql-api; la auth de esa llamada
 * a traves del transport v2 esta SIN VERIFICAR. Se registra DESHABILITADO. Para habilitar:
 * confirmar que el backend reubica tags a graphql-api (preferido) O que la llamada al backend viejo
 * funciona autenticada via getTransport(), luego flipear enabled:true (flag __OFFLINE_TAGS_PULL__).
 * Hasta entonces la creacion de tasks offline (F5/F6) DEBE tolerar c_tags vacio.
 */
const cTags = {
  name: 'tags',
  table: 'c_tags',
  ttl: TIER_B_TTLS.tags,
  enabled: tagsPullEnabled(), // default false (gated); el flag puede flipearlo sin tocar codigo
  keyColumns: ['id'],
  columns: ['id', 'name', 'data'],
  async fetchPage () {
    const { old } = await apiBases()
    const res = await getTransport().get(`${old}action-plans/tags`, PULL_TIMEOUT)
    return res.data
  },
  mapRow (r) {
    return { id: r.id, name: r.name, data: JSON.stringify(r) }
  }
}

/**
 * 10) c_catalog_elements — LAZY. NUNCA en bootstrap/revalidate; solo on-demand por catalog_id
 * (hasta ~50k por catalogo). El catalogId viaja en scope.catalogId. Solo se invoca cuando la UI
 * pide explicitamente los elementos de un catalogo.
 */
const cCatalogElements = {
  name: 'catalog_elements',
  table: 'c_catalog_elements',
  ttl: TIER_B_TTLS.catalog_elements,
  lazy: true,
  keyColumns: ['id'],
  columns: ['id', 'catalog_id', 'data'],
  scopedDelete (scope) {
    return { whereSql: 'catalog_id=?', whereParams: [scope && scope.catalogId] }
  },
  async fetchPage (scope, page) {
    const catalogId = scope && scope.catalogId
    const { gql } = await apiBases()
    const res = await getTransport().get(`${gql}catalogs/${catalogId}/elements`, { ...PULL_TIMEOUT, params: { limit: PAGE_LIMIT, page } })
    return res.data
  },
  mapRow (r, scope) {
    return { id: r.id, catalog_id: (scope && scope.catalogId) != null ? scope.catalogId : r.catalog_id, data: JSON.stringify(r) }
  }
}

const ALL_DESCRIPTORS = [
  cForms, cFormGroups, cFormItems, cLocations, cUsers, cRoles, cAreas, cHierarchies, cTags, cCatalogElements
]

/**
 * registerAllCatalogs() — registra los 10 descriptores via registerCatalog de 05-01. registerCatalog
 * sobre-escribe por nombre (idempotente: re-llamar no duplica). Llamar una vez en el boot (o lo
 * llaman los tests tras _clearRegistry).
 */
export function registerAllCatalogs () {
  for (const def of ALL_DESCRIPTORS) registerCatalog(def)
  // Cablea el hook post-forms del engine (B2) sin crear un import estatico catalogs←pull-engine.
  setPostFormsHook((scope, opts) => prefetchFormChildren(scope, opts))
}

// Cap DEDICADO del prefetch (deuda de tuning 2026-06): antes el cap reusaba PAGE_LIMIT (200) y un
// operador con ~390 forms asignados pagaba ~200 fan-outs de red secuenciales por bootstrap (minutos
// con backend lento). 50 forms calientes por corrida acota el bootstrap a segundos-decenas; el resto
// queda lazy on-demand Y las corridas siguientes (cada bootstrap) avanzan sobre los que falten
// gracias a la marca de frescura por-form (ver abajo): el warm-up es INCREMENTAL, no truncado.
export const PREFETCH_FORMS_CAP = 50

// Frescura por-form: meta 'prefetch:form:<id>' = epoch ms del ultimo warm-up COMPLETO (groups +
// items sin error). Mismo TTL que el catalogo de groups (6h).
export const PREFETCH_FORM_TTL = TIER_B_TTLS.form_groups

/**
 * prefetchFormChildren(scope, {db, limit, now}) — la garantia PULL-02 (B2): tras pulear c_forms,
 * deja OFFLINE los groups+items de los forms ASIGNADOS del operador, para que pueda abrir CUALQUIER
 * form asignado offline aunque nunca lo haya abierto online.
 *
 * - Resuelve scope via getScope (05-01); undefined => console.warn + return (best-effort, no throw).
 * - Lee los form ids de la tabla c_forms RECIEN pulleada (SON el set operator-scoped /api/forms =
 *   los asignados, NO el catalogo entero de la org), priorizando los de updated_at mas reciente.
 * - SKIP-FRESCOS: un form se salta si su marca 'prefetch:form:<id>' esta dentro del TTL *Y* aun
 *   tiene groups cacheados (si la cache local fue barrida, la marca sola NO basta — se recalienta).
 *   Asi corridas repetidas no re-bajan todo y el cap avanza sobre forms PENDIENTES.
 * - Cap en `limit` (default PREFETCH_FORMS_CAP): si los stale exceden, se calientan los primeros
 *   `limit` + console.warn del resto (que cubriran corridas siguientes o el lazy on-demand).
 * - Por CADA formId (SECUENCIAL, best-effort): pullCatalog('form_groups', {...scope, formId}, {db});
 *   por CADA group id resultante: pullCatalog('form_items', {...scope, groupId}, {db}). Todo en
 *   try/catch por-form (el fallo de un form NO aborta el resto). Nunca Promise.all-storm. La marca
 *   de frescura SOLO se escribe si el form completo (groups + todos sus items) bajo sin error —
 *   un warm-up parcial se reintenta en la siguiente corrida.
 *
 * Call site: bootstrapTierB (05-01) tiene el hook reservado tras pulear c_forms. Se exporta para que
 * la edicion de boot lo cablee (orden: c_forms pull → prefetchFormChildren).
 */
export async function prefetchFormChildren (scope, { db = StorageAdapter, limit = PREFETCH_FORMS_CAP, now = Date.now() } = {}) {
  const s = getScope(scope)
  if (s === undefined || s === null) {
    dbgWarn('pull', 'prefetchFormChildren skipped, no scope')
    return { skipped: 'no-scope' }
  }

  // Los forms del operador = lo que c_forms acaba de pulear; los mas recientes primero.
  let formIds = []
  try {
    const orgId = s.organizationId
    const res = orgId != null
      ? await db.query('SELECT id FROM c_forms WHERE organization_id=? OR organization_id IS NULL ORDER BY updated_at DESC', [orgId])
      : await db.query('SELECT id FROM c_forms ORDER BY updated_at DESC')
    formIds = ((res && res.values) || []).map((r) => r.id).filter((id) => id != null)
  } catch (error) {
    dbgWarn('pull', 'prefetchFormChildren: could not read c_forms', error)
    return { skipped: 'no-forms' }
  }

  // SKIP-FRESCOS: marca dentro del TTL + groups presentes en cache.
  let skippedFresh = 0
  const stale = []
  for (const formId of formIds) {
    if (await isFormWarm(db, formId, now)) { skippedFresh += 1; continue }
    stale.push(formId)
  }

  let toWarm = stale
  if (toWarm.length > limit) {
    dbgWarn('pull', `prefetchFormChildren: ${toWarm.length} stale forms exceeds cap ${limit}; warming first ${limit}, rest next run / lazy`)
    toWarm = toWarm.slice(0, limit)
  }

  let forms = 0
  let groups = 0
  for (const formId of toWarm) {
    try {
      await pullCatalog('form_groups', { ...s, formId }, { db })
      forms += 1
      let groupIds = []
      try {
        const gres = await db.query('SELECT id FROM c_form_groups WHERE form_id=?', [formId])
        groupIds = ((gres && gres.values) || []).map((r) => r.id).filter((id) => id != null)
      } catch (gerr) {
        dbgWarn('pull', 'prefetchFormChildren: could not read c_form_groups: ' + formId, gerr)
      }
      let complete = true
      for (const groupId of groupIds) {
        try {
          await pullCatalog('form_items', { ...s, groupId }, { db })
          groups += 1
        } catch (ierr) {
          complete = false // warm-up parcial: NO se marca fresco, se reintenta en la proxima corrida.
          dbgWarn('pull', `prefetchFormChildren: items failed form=${formId} group=${groupId}`, ierr)
        }
      }
      if (complete) await markFormWarm(db, formId, now)
    } catch (ferr) {
      // El fallo de un form NO debe abortar el resto (failure isolation per form).
      dbgWarn('pull', 'prefetchFormChildren: groups failed: ' + formId, ferr)
    }
  }
  return { forms, groups, skippedFresh }
}

/** Form "caliente" = marca de frescura dentro del TTL Y groups aun cacheados (anti cache barrida). */
async function isFormWarm (db, formId, now) {
  try {
    const m = await db.query('SELECT value FROM meta WHERE key=?', ['prefetch:form:' + formId])
    const v = m && m.values && m.values[0] && m.values[0].value
    if (!v || (now - Number(v)) > PREFETCH_FORM_TTL) return false
    const g = await db.query('SELECT 1 AS one FROM c_form_groups WHERE form_id=? LIMIT 1', [formId])
    return !!(g && g.values && g.values.length)
  } catch {
    return false // ante la duda, recalentar (best-effort, nunca lanzar).
  }
}

async function markFormWarm (db, formId, now) {
  try {
    await db.run(
      'INSERT INTO meta (key, value) VALUES (?,?) ON CONFLICT(key) DO UPDATE SET value=excluded.value',
      ['prefetch:form:' + formId, String(now)],
      true
    )
  } catch (e) {
    dbgWarn('pull', 'prefetchFormChildren: mark failed: ' + formId, e) // no bloquea
  }
}

// ─── helpers internos ─────────────────────────────────────────────────────────

/** Tolera {data:[...]} o array plano (Open Q3, hierarchies shape TBD). */
function unwrapArray (body) {
  if (Array.isArray(body)) return body
  if (body && Array.isArray(body.data)) return body.data
  return []
}

/** Quita el marcador interno __kind antes de serializar data. */
function stripInternal (r) {
  const out = { ...r }
  delete out.__kind
  return out
}
